import * as React from 'react';
import { createRoute, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const ideaDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ideas/$id',
  component: IdeaDetailPage,
});

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function IdeaDetailPage(): JSX.Element {
  const { id } = useParams({ from: ideaDetailRoute.id });
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);
  const [commentBody, setCommentBody] = React.useState('');
  const peopleQuery = useQuery({ queryKey: ['people-simple'], queryFn: () => api.listPeopleSimple() });

  const detailQuery = useQuery({ queryKey: ['idea', id], queryFn: () => api.getIdea(id) });
  const commentsQuery = useQuery({
    queryKey: ['idea', id, 'comments'],
    queryFn: () => api.listIdeaComments(id),
  });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['idea', id] }),
      queryClient.invalidateQueries({ queryKey: ['idea', id, 'comments'] }),
      queryClient.invalidateQueries({ queryKey: ['ideas'] }),
    ]);
  };

  const vote = useMutation({
    mutationFn: () => api.voteIdea(id),
    onSuccess: async () => { setError(null); await invalidate(); },
    onError: (err) => setError(toMessage(err)),
  });

  const addComment = useMutation({
    mutationFn: () => api.addIdeaComment(id, { body: commentBody.trim() }),
    onSuccess: async () => {
      setCommentBody('');
      setError(null);
      await invalidate();
    },
    onError: (err) => setError(toMessage(err)),
  });

  const d = detailQuery.data;
  const comments = commentsQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            {d?.title ?? 'Loading…'}
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            {d
              ? `${d.category} · sponsor ${d.sponsorName ?? d.sponsorId} · ${d.voteCount} vote(s)${
                  d.convertedProjectId ? ` · project ${d.convertedProjectId}` : ''
                }`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {d ? <Badge>{d.status}</Badge> : null}
          {d && d.visibility === 'c_suite_and_sponsor' ? (
            <Badge variant="muted">Confidential</Badge>
          ) : null}
          <Button variant="outline" onClick={() => vote.mutate()} disabled={vote.isPending}>
            {d?.votedByMe ? 'Unvote' : 'Vote'} · {d?.voteCount ?? 0}
          </Button>
          {d ? <ActionsForStatus detail={d} invalidate={invalidate} setError={setError} /> : null}
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Pitch</CardTitle>
          <CardDescription>Immutable once proposed; edit only in draft.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="whitespace-pre-wrap text-sm leading-relaxed">
            {d?.pitch ?? 'Loading…'}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Decision log</CardTitle>
          <CardDescription>Append-only. Every rationale is preserved.</CardDescription>
        </CardHeader>
        <CardContent>
          {!d || d.decisions.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No decisions yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {d.decisions.map((dec) => (
                <li key={dec.id} className="py-3 text-sm">
                  <div className="flex items-center gap-2 font-medium">
                    <span>{dec.fromStatus} → {dec.toStatus}</span>
                    <Badge variant="muted">{dec.kind}</Badge>
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    {dec.decidedByName ?? dec.decidedBy} ·{' '}
                    {new Date(dec.createdAt).toISOString().slice(0, 19).replace('T', ' ')} UTC
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-sm">{dec.rationale}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Discussion</CardTitle>
          <CardDescription>
            Mention someone to raise a queue item for them (72h SLA). Mentions render as <code>@[Name](id)</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Textarea
              rows={3}
              value={commentBody}
              onChange={(e) => setCommentBody(e.currentTarget.value)}
              placeholder="Add a comment…"
            />
            <div className="flex justify-between gap-2">
              <div className="w-64">
                <Select
                  value=""
                  aria-label="Mention someone"
                  onChange={(e) => {
                    const p = (peopleQuery.data ?? []).find((x) => x.id === e.currentTarget.value);
                    if (!p) return;
                    setCommentBody((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}@[${p.name}](${p.id}) `);
                  }}
                >
                  <option value="">Mention someone…</option>
                  {(peopleQuery.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              </div>
              <Button
                onClick={() => addComment.mutate()}
                disabled={commentBody.trim() === '' || addComment.isPending}
              >
                {addComment.isPending ? 'Posting…' : 'Comment'}
              </Button>
            </div>
          </div>

          {comments.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No comments yet.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {comments.map((c) => (
                <li key={c.id} className="rounded-lg border border-[hsl(var(--border))] p-3 text-sm">
                  <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                    <span className="font-medium text-[hsl(var(--foreground))]">
                      {c.authorName ?? c.authorId}
                    </span>
                    <span>·</span>
                    <span>{new Date(c.createdAt).toISOString().slice(0, 19).replace('T', ' ')} UTC</span>
                    {c.mentions.length > 0 ? (
                      <span>
                        · cc {c.mentions.map((m) => m.personName ?? m.personId).join(', ')}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap">{c.body}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}

function ActionsForStatus({
  detail,
  invalidate,
  setError,
}: {
  detail: NonNullable<ReturnType<typeof api.getIdea> extends Promise<infer T> ? T : never>;
  invalidate: () => Promise<void>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}): JSX.Element | null {
  const [rationale, setRationale] = React.useState('');
  const [revisitOn, setRevisitOn] = React.useState(addDaysIso(todayIso(), 30));
  const [decisionOwnerId, setDecisionOwnerId] = React.useState('');
  const [decisionDueOn, setDecisionDueOn] = React.useState(addDaysIso(todayIso(), 14));
  const [projectCode, setProjectCode] = React.useState('');
  const [projectName, setProjectName] = React.useState('');
  const [budgetRupees, setBudgetRupees] = React.useState('');
  const [budgetReason, setBudgetReason] = React.useState('');
  const [reviewOffset, setReviewOffset] = React.useState('90');
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [parkOpen, setParkOpen] = React.useState(false);
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [proposeOpen, setProposeOpen] = React.useState(false);
  const [resurfaceOpen, setResurfaceOpen] = React.useState(false);

  const wrap = <T,>(fn: () => Promise<T>, closer: () => void): (() => Promise<void>) =>
    async () => {
      try {
        await fn();
        setRationale('');
        setError(null);
        closer();
        await invalidate();
      } catch (err) {
        setError(toMessage(err));
      }
    };

  const id = detail.id;
  switch (detail.status) {
    case 'draft':
      return (
        <Dialog open={proposeOpen} onOpenChange={setProposeOpen}>
          <DialogTrigger asChild>
            <Button>Propose</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Propose idea</DialogTitle>
              <DialogDescription>Locks the pitch text; enters the review queue.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Rationale</Label>
                <Textarea rows={3} value={rationale} onChange={(e) => setRationale(e.currentTarget.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={wrap(
                  () => api.proposeIdea(id, { rationale }),
                  () => setProposeOpen(false),
                )}
                disabled={rationale.trim() === ''}
              >
                Propose
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    case 'proposed':
      return (
        <div className="flex items-center gap-2">
          <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
            <DialogTrigger asChild>
              <Button>Pick up for review</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Pick up for review</DialogTitle>
                <DialogDescription>Assign a decision owner and target date.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Decision owner (personId)</Label>
                  <Input value={decisionOwnerId} onChange={(e) => setDecisionOwnerId(e.currentTarget.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Decision due on</Label>
                  <Input type="date" value={decisionDueOn} onChange={(e) => setDecisionDueOn(e.currentTarget.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Rationale</Label>
                  <Textarea rows={3} value={rationale} onChange={(e) => setRationale(e.currentTarget.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={wrap(
                    () => api.pickUpIdea(id, { rationale, decisionOwnerId, decisionDueOn }),
                    () => setReviewOpen(false),
                  )}
                  disabled={rationale.trim() === '' || decisionOwnerId.trim() === ''}
                >
                  Pick up
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <ParkDialog open={parkOpen} setOpen={setParkOpen} rationale={rationale} setRationale={setRationale}
            revisitOn={revisitOn} setRevisitOn={setRevisitOn} wrap={wrap} id={id} />
          <RejectDialog open={rejectOpen} setOpen={setRejectOpen} rationale={rationale} setRationale={setRationale}
            wrap={wrap} id={id} />
        </div>
      );
    case 'under_review':
      return (
        <div className="flex items-center gap-2">
          <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
            <DialogTrigger asChild>
              <Button>Approve → project</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Approve idea</DialogTitle>
                <DialogDescription>
                  Creates an internal_initiative Project with the specified budget. Sets review-due
                  date. Ideas never carry money.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label>Project code</Label>
                    <Input value={projectCode} onChange={(e) => setProjectCode(e.currentTarget.value)} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Project name</Label>
                    <Input value={projectName} onChange={(e) => setProjectName(e.currentTarget.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label>Budget (INR)</Label>
                    <Input type="number" min={1} value={budgetRupees} onChange={(e) => setBudgetRupees(e.currentTarget.value)} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Review offset (days)</Label>
                    <Input type="number" min={1} value={reviewOffset} onChange={(e) => setReviewOffset(e.currentTarget.value)} />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Budget reason</Label>
                  <Input value={budgetReason} onChange={(e) => setBudgetReason(e.currentTarget.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Approval rationale</Label>
                  <Textarea rows={3} value={rationale} onChange={(e) => setRationale(e.currentTarget.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={wrap(
                    () =>
                      api.approveIdea(id, {
                        rationale,
                        projectCode,
                        projectName,
                        budgetPaise: Math.round(parseFloat(budgetRupees) * 100),
                        budgetReason,
                        reviewOffsetDays: parseInt(reviewOffset, 10),
                      }),
                    () => setApproveOpen(false),
                  )}
                  disabled={
                    rationale.trim() === '' ||
                    projectCode.trim() === '' ||
                    projectName.trim() === '' ||
                    budgetRupees.trim() === '' ||
                    budgetReason.trim() === ''
                  }
                >
                  Approve & create project
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <ParkDialog open={parkOpen} setOpen={setParkOpen} rationale={rationale} setRationale={setRationale}
            revisitOn={revisitOn} setRevisitOn={setRevisitOn} wrap={wrap} id={id} />
          <RejectDialog open={rejectOpen} setOpen={setRejectOpen} rationale={rationale} setRationale={setRationale}
            wrap={wrap} id={id} />
        </div>
      );
    case 'parked':
      return (
        <Dialog open={resurfaceOpen} onOpenChange={setResurfaceOpen}>
          <DialogTrigger asChild>
            <Button>Resurface</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resurface parked idea</DialogTitle>
              <DialogDescription>Moves back to Proposed for another decision cycle.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-1.5">
              <Label>Rationale</Label>
              <Textarea rows={3} value={rationale} onChange={(e) => setRationale(e.currentTarget.value)} />
            </div>
            <DialogFooter>
              <Button
                onClick={wrap(
                  () => api.resurfaceIdea(id, { rationale }),
                  () => setResurfaceOpen(false),
                )}
                disabled={rationale.trim() === ''}
              >
                Resurface
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    default:
      return null;
  }
}

function ParkDialog(props: {
  open: boolean;
  setOpen: (b: boolean) => void;
  rationale: string;
  setRationale: (s: string) => void;
  revisitOn: string;
  setRevisitOn: (s: string) => void;
  wrap: <T>(fn: () => Promise<T>, closer: () => void) => () => Promise<void>;
  id: string;
}): JSX.Element {
  return (
    <Dialog open={props.open} onOpenChange={props.setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Park</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Park idea</DialogTitle>
          <DialogDescription>Revisit date is required — the idea auto-resurfaces then.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Revisit on</Label>
            <Input type="date" value={props.revisitOn} onChange={(e) => props.setRevisitOn(e.currentTarget.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Rationale</Label>
            <Textarea rows={3} value={props.rationale} onChange={(e) => props.setRationale(e.currentTarget.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={props.wrap(
              () => api.parkIdea(props.id, { rationale: props.rationale, revisitOn: props.revisitOn }),
              () => props.setOpen(false),
            )}
            disabled={props.rationale.trim() === '' || props.revisitOn === ''}
          >
            Park
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog(props: {
  open: boolean;
  setOpen: (b: boolean) => void;
  rationale: string;
  setRationale: (s: string) => void;
  wrap: <T>(fn: () => Promise<T>, closer: () => void) => () => Promise<void>;
  id: string;
}): JSX.Element {
  return (
    <Dialog open={props.open} onOpenChange={props.setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive">Reject</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject idea</DialogTitle>
          <DialogDescription>Terminal. Rationale is permanent.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label>Rationale</Label>
          <Textarea rows={3} value={props.rationale} onChange={(e) => props.setRationale(e.currentTarget.value)} />
        </div>
        <DialogFooter>
          <Button
            variant="destructive"
            onClick={props.wrap(
              () => api.rejectIdea(props.id, { rationale: props.rationale }),
              () => props.setOpen(false),
            )}
            disabled={props.rationale.trim() === ''}
          >
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
